import React, { useState } from 'react';
import { useUser, UserData } from '../contexts/UserContext';
import { ShieldAlertIcon, XIcon, PlusIcon, SearchIcon, UserIcon, HomeIcon } from 'lucide-react';
// Mock users for the search modal
const MOCK_USERS: UserData[] = [{
  name: 'Alice Smith',
  email: 'alice@dealer.com',
  role: 'dealer',
  companyName: 'AgriTech',
  companyLogo: null,
  officialCountry: 'USA'
}, {
  name: 'Bob Jones',
  email: 'bob@customer.com',
  role: 'customer',
  companyName: 'FarmCorp',
  companyLogo: null,
  officialCountry: 'Canada'
}, {
  name: 'Carlos Ruiz',
  email: 'carlos@aisco.com',
  role: 'sales-manager',
  companyName: 'Aisco',
  companyLogo: null,
  officialCountry: 'Spain'
}, {
  name: 'Diana Prince',
  email: 'diana@aisco.com',
  role: 'region-admin',
  companyName: 'Aisco',
  companyLogo: null,
  officialCountry: 'UK'
}];
export function ImpersonationBar() {
  const {
    isImpersonating,
    impersonatedUsers,
    originalUser,
    email: currentEmail,
    impersonateUser,
    returnToOriginal,
    stopImpersonation
  } = useUser();
  const isOwnerActive = originalUser ? currentEmail === originalUser.email : false;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  if (!isImpersonating) return null;
  const filteredUsers = MOCK_USERS.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()));
  return <>
      <div className="bg-amber-600/90 text-white px-4 py-2 flex items-center justify-between z-[100] relative shadow-md" data-id="element-3145">
        <div className="flex items-center gap-4 overflow-x-auto no-scrollbar" data-id="element-3146">
          <div className="flex items-center gap-2 flex-shrink-0 font-medium" data-id="element-3147">
            <ShieldAlertIcon size={16} data-id="element-3148" />
            <span data-id="element-3149">Impersonation Active</span>
          </div>

          <div className="h-4 w-px bg-white/30 flex-shrink-0" data-id="element-3150" />

          <div className="flex items-center gap-2" data-id="element-3151">
            {/* Owner badge — original admin who started the impersonation */}
            {originalUser && <button onClick={returnToOriginal} className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap border-2 ${isOwnerActive ? 'bg-white text-amber-700 border-white shadow-md' : 'bg-transparent border-dashed border-white/60 hover:bg-white/10 text-white'}`} title={`Return to ${originalUser.name} (your account)`} data-id="element-3152">
                <HomeIcon size={12} data-id="element-3153" />
                <span className="font-semibold" data-id="element-3154">{originalUser.name}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${isOwnerActive ? 'bg-amber-100 text-amber-800' : 'bg-white/20 text-white'}`} data-id="element-3155">
                  Owner
                </span>
              </button>}

            {originalUser && impersonatedUsers.length > 0 && <div className="h-4 w-px bg-white/30 flex-shrink-0" data-id="element-3156" />}

            {impersonatedUsers.map(user => <button key={user.email} onClick={() => impersonateUser(user)} className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap border ${currentEmail === user.email ? 'bg-white text-amber-700 border-white shadow-sm' : 'bg-black/10 border-white/20 hover:bg-black/20 text-white'}`} data-id="element-3157">
                <UserIcon size={12} data-id="element-3158" />
                <span data-id="element-3159">{user.name}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${currentEmail === user.email ? 'bg-amber-100 text-amber-800' : 'bg-black/20 text-white/90'}`} data-id="element-3160">
                  {user.role.replace('-', ' ')}
                </span>
              </button>)}

            <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center w-7 h-7 rounded-full bg-black/10 hover:bg-black/20 border border-white/20 transition-colors flex-shrink-0" title="Impersonate another user" data-id="element-3161">
              <PlusIcon size={14} data-id="element-3162" />
            </button>
          </div>
        </div>

        <button onClick={stopImpersonation} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ml-4" data-id="element-3163">
          <XIcon size={14} data-id="element-3164" />
          Exit
        </button>
      </div>

      {isModalOpen && <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-3165">
          <div className="bg-[#0f1d3a] rounded-xl shadow-2xl border border-white/10 w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]" data-id="element-3166">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between" data-id="element-3167">
              <h2 className="text-white font-semibold text-lg" data-id="element-3168">
                Impersonate User
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-blue-300 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors" data-id="element-3169">
                <XIcon size={20} data-id="element-3170" />
              </button>
            </div>

            <div className="p-4 border-b border-white/10" data-id="element-3171">
              <div className="relative" data-id="element-3172">
                <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-3173" />
                <input type="text" placeholder="Search users by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus data-id="element-3174" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 no-scrollbar" data-id="element-3175">
              {filteredUsers.length === 0 ? <div className="p-8 text-center text-blue-300/70 text-sm" data-id="element-3176">
                  No users found matching "{searchQuery}"
                </div> : <div className="space-y-1" data-id="element-3177">
                  {filteredUsers.map(user => <button key={user.email} onClick={() => {
              impersonateUser(user);
              setIsModalOpen(false);
            }} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors text-left group" data-id="element-3178">
                      <div className="flex items-center gap-3" data-id="element-3179">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-3180">
                          <span className="text-sm font-bold text-white" data-id="element-3181">
                            {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                        <div data-id="element-3182">
                          <p className="text-white font-medium text-sm group-hover:text-blue-200 transition-colors" data-id="element-3183">
                            {user.name}
                          </p>
                          <p className="text-blue-300/70 text-xs" data-id="element-3184">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-3185">
                        <span className="px-2 py-1 rounded bg-white/10 text-blue-200 text-[10px] uppercase font-semibold tracking-wider" data-id="element-3186">
                          {user.role.replace('-', ' ')}
                        </span>
                        <span className="text-blue-300/50 text-xs mt-1" data-id="element-3187">
                          {user.companyName}
                        </span>
                      </div>
                    </button>)}
                </div>}
            </div>
          </div>
        </div>}
    </>;
}